import {
  trackFeatureUsage,
  trackFlowComplete,
  trackFlowStep,
  type ConversionSurface,
} from "@/utils/gtm";
import type { RailStepId } from "./types";

/**
 * This run's identity in the shared flow_step / flow_complete events.
 *
 * One pair of events covers both guided runs; flow_name is what separates the
 * logged-in onboarding rail from the logged-out demo.
 */
const ONBOARDING_FLOW_NAME = "onboarding";
const ONBOARDING_SURFACE: ConversionSurface = "onboarding_rail";

/**
 * Two tiers, the same split the demo guide uses.
 *
 * The rail's progress — each step, and the run finishing — goes out as the
 * shared flow_step / flow_complete events, because that is the series anyone
 * actually charts and it should not have to be reassembled from a generic
 * event's free-text detail field. What happens AROUND the run — exits and
 * resumes — stays on feature_usage.
 *
 * "onboarding" is the rail's name alone, as it is flow_step's flow_name. The
 * welcome modal and the checklist each have their own — see trackWelcomeSetup
 * and trackOnboardingChecklist.
 */
export type OnboardingAnalyticsAction =
  // A skipped step is not reported here. It rides on flow_step with
  // `skipped: true`, so the step stream stays unbroken and a funnel can be read
  // from one series — see trackFlowStep.
  | "rail_exited"
  // Only reachable from the checklist's "Pick it back up", so this is also
  // the count of runs the checklist brought back — no checklist event for it.
  | "rail_resumed";

/**
 * The "Getting started" checklist, on a feature_name of its own.
 *
 * It shared "onboarding" with the rail, and its task_done was mostly not
 * about it: detection runs while the rail is on screen too, and four of the
 * six task ids are the rail's "have they ever" record with no checklist row.
 * So task_done now means one of the checklist's own rows, done while the
 * checklist was the surface on screen — see markTaskDone.
 */
export type OnboardingChecklistAction = "opened" | "task_done" | "dismissed";

export function trackOnboardingChecklist(
  action: OnboardingChecklistAction,
  detail?: string
) {
  trackFeatureUsage({
    feature_name: "onboarding_checklist",
    feature_action: action,
    ...(detail && { feature_detail: detail }),
  });
}

/**
 * Onboarding funnel events, routed through the existing GA4 feature_usage
 * event so they land in the same GTM container with user props attached.
 */
export function trackOnboarding(
  action: OnboardingAnalyticsAction,
  detail?: string
) {
  trackFeatureUsage({
    feature_name: "onboarding",
    feature_action: action,
    ...(detail && { feature_detail: detail }),
  });
}

/**
 * The first-login setup modal, on a feature_name of its own.
 *
 * It used to share "onboarding" with the checklist and the rail, so a count of
 * onboarding rows was three surfaces added together — and `template_applied`
 * was also fired by the New Tab template gallery, for anyone, at any time,
 * which made "how many trialists finished setup" unanswerable. Still
 * feature_usage, so the GTM container needs no change.
 *
 * `completed` is the one completion event, whichever route built the
 * dashboard; its detail says which ("concierge" or a template id).
 */
export type WelcomeSetupAction =
  | "started"
  | "resumed"
  | "step_completed"
  | "description_submitted"
  | "watchlists_selected"
  | "concierge_requested"
  | "concierge_failed"
  | "concierge_rejected"
  | "completed"
  | "skipped";

export function trackWelcomeSetup(action: WelcomeSetupAction, detail?: string) {
  trackFeatureUsage({
    feature_name: "welcome_setup",
    feature_action: action,
    ...(detail && { feature_detail: detail }),
  });
}

/**
 * Why a concierge plan request failed, as a small fixed set: the HTTP status
 * when the server answered (429 rate limit, 503 paused/unavailable, 502 the
 * model's plan failed validation), otherwise how the request died. Read from
 * the axios error's shape so this module stays free of an axios import.
 */
export function conciergeFailureReason(error: unknown): string {
  const e = error as { code?: string; response?: { status?: number } } | null;
  if (e?.response?.status) return String(e.response.status);
  if (e?.code === "ECONNABORTED" || e?.code === "ETIMEDOUT") return "timeout";
  if (e?.code === "ERR_NETWORK") return "network";
  return "other";
}

/**
 * One rail step reached its end, however it ended.
 *
 * Emitted for skipped steps too — see trackFlowStep for why the step stream has
 * to be unbroken for a funnel to be readable.
 */
export function trackRailStep(params: {
  stepId: RailStepId;
  /** Zero-based position in the run; converted to 1-based for the event. */
  stepIndex: number;
  stepTotal: number;
  skipped: boolean;
}) {
  trackFlowStep({
    flow_name: ONBOARDING_FLOW_NAME,
    step_id: params.stepId,
    step_index: params.stepIndex + 1,
    step_total: params.stepTotal,
    skipped: params.skipped,
    surface: ONBOARDING_SURFACE,
    // The rail only ever runs for an authenticated trialist, but the dimension
    // travels anyway so flow rows from both runs union without a null.
    authenticated: true,
  });
}

/** The rail reached the end. Once per run, in addition to the final step. */
export function trackRailComplete(params: {
  stepsCompleted: number;
}) {
  trackFlowComplete({
    flow_name: ONBOARDING_FLOW_NAME,
    steps_completed: params.stepsCompleted,
    surface: ONBOARDING_SURFACE,
    authenticated: true,
  });
}
